import { ChangeDetectionStrategy, Component, OnInit, signal, viewChild } from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatPaginator, MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { Router } from "@angular/router";
import { catchError, debounceTime, finalize, map, Observable, of, takeUntil } from "rxjs";

import { NotificationsApiService } from "../../services/notifications-api.service";
import { NotificationsSidebarService } from "../../services/notifications-sidebar.service";
import { TNotification, TNotificationStrategy, TPaginatedNotifications } from "../../types/notification.type";
import { NotificationItemComponent } from "../notification-item/notification-item.component";

import { DestroyService } from "@app/core/services/destroy.service";
import { NotificationHubService } from "@app/core/signalR/notification-hub.service";
import { TranslatePipe } from "@ngx-translate/core";

const NEW_NOTIFICATION_DEBOUNCE_MS = 300;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE];
const EMPTY_NOTIFICATIONS_PAGE: TPaginatedNotifications = {
  items: [],
  totalCount: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

@Component({
  selector: "under-ui-notifications-panel",
  templateUrl: "./notifications-panel.component.html",
  styleUrl: "./notifications-panel.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  providers: [DestroyService],
  imports: [
    MatButtonModule,
    MatIconModule,
    MatPaginatorModule,
    NotificationItemComponent,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    TranslatePipe,
  ],
})
export class NotificationsPanelComponent implements OnInit {
  notifications = signal<TNotification[]>([]);
  isLoading = signal(true);
  totalCount = signal(0);
  pageSize = signal(DEFAULT_PAGE_SIZE);
  pageIndex = signal(0);
  unreadOnly = signal(false);

  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  paginator = viewChild.required(MatPaginator);

  private readonly pendingReadIds = new Set<string>();

  constructor(
    private readonly router: Router,
    private readonly destroy$: DestroyService,
    private readonly notificationsSidebarService: NotificationsSidebarService,
    private readonly notificationsApiService: NotificationsApiService,
    private readonly notificationHubService: NotificationHubService,
  ) {}

  ngOnInit(): void {
    this.onLoadingNotifications();
    this.loadUnreadCount();

    this.notificationHubService.bellNotification$
      .pipe(debounceTime(NEW_NOTIFICATION_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe(() => this.onLoadingNotifications());
  }

  onClosePanel(): void {
    this.notificationsSidebarService.close();
  }

  private onLoadingNotifications(): void {
    this.isLoading.set(true);

    this.notificationsApiService
      .getNotifications({
        page: this.pageIndex() + 1,
        pageSize: this.pageSize(),
        filters: { unreadOnly: this.unreadOnly() },
      })
      .pipe(
        map(res => res.data),
        catchError(() => of(EMPTY_NOTIFICATIONS_PAGE)),
        finalize(() => this.isLoading.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe(response => {
        this.notifications.set(response.items);
        this.totalCount.set(response.totalCount);

        if (!response.items.length && response.totalCount > 0 && this.pageIndex() > 0) {
          this.pageIndex.set(0);
          this.onLoadingNotifications();
        }
      });
  }

  handlePageEvent(e: PageEvent): void {
    this.pageSize.set(e.pageSize);
    this.pageIndex.set(e.pageIndex);

    this.onLoadingNotifications();
  }

  markAllAsRead(): void {
    this.isLoading.set(true);

    this.notificationsApiService
      .markAllAsRead()
      .pipe(
        map(response => !!response),
        catchError(() => of(false)),
        finalize(() => this.isLoading.set(false)),
        takeUntil(this.destroy$),
      )
      .subscribe(success => {
        if (!success) {
          return;
        }

        this.notificationsSidebarService.resetNotificationsCount();

        if (this.unreadOnly()) {
          this.pageIndex.set(0);
          this.onLoadingNotifications();

          return;
        }

        this.notifications.update(items => items.map(item => ({ ...item, isRead: true })));
      });
  }

  protected onUnreadOnlyChange(unreadOnly: boolean): void {
    this.unreadOnly.set(unreadOnly);
    this.pageIndex.set(0);

    this.onLoadingNotifications();
  }

  protected onExecuteAction($event: TNotificationStrategy): void {
    if ($event.action !== "REDIRECT" || !$event.buildURL) {
      return;
    }

    if ($event.isRead) {
      this.onRedirect($event.buildURL);

      return;
    }

    if (this.pendingReadIds.has($event.id)) {
      return;
    }

    const targetUrl = $event.buildURL;

    this.markAsRead($event.id).subscribe(success => {
      if (success) {
        this.applyReadState($event.id);
        this.notificationsSidebarService.onDecrementCount();
      }

      this.onRedirect(targetUrl);
    });
  }

  protected onMarkAsRead($event: TNotificationStrategy): void {
    if ($event.isRead || this.pendingReadIds.has($event.id)) {
      return;
    }

    this.markAsRead($event.id).subscribe(success => {
      if (!success) {
        return;
      }

      this.applyReadState($event.id);
      this.notificationsSidebarService.onDecrementCount();
    });
  }

  private onRedirect(url: string): void {
    this.router.navigateByUrl(url);
    this.onClosePanel();
  }

  private applyReadState(notificationId: string): void {
    if (this.unreadOnly()) {
      this.onLoadingNotifications();

      return;
    }

    this.notifications.update(items =>
      items.map(item => (item.id === notificationId ? { ...item, isRead: true } : item)),
    );
  }

  private loadUnreadCount(): void {
    this.unreadCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => this.notificationsSidebarService.setNotificationsCount(count));
  }

  private markAsRead(notificationId: string): Observable<boolean> {
    this.pendingReadIds.add(notificationId);

    return this.notificationsApiService.markasRead(notificationId).pipe(
      map((response) => response.data),
      catchError(() => of(false)),
      finalize(() => this.pendingReadIds.delete(notificationId)),
      takeUntil(this.destroy$),
    );
  }

  private unreadCount(): Observable<number> {
    return this.notificationsApiService.fetchUnreadCount().pipe(
      map((response) => response.data),
      catchError(() => of(0)),
    )
  }
}
